import { ChevronDown, Mic, MicOff, Phone, PhoneOff, X } from 'lucide-react';
import React, { useEffect, useRef } from 'react';

import { playEndTone, startRingback, startRingtone } from './call-tones';

import type { Softphone, SoftphoneEndReason, SoftphoneState } from './use-whatsapp-call';

import { isPushCovering } from '@/features/push/web-push-client';

/** WhatsApp dark palette (desktop app call window). */
const WA = {
  bg: 'bg-[#0b141a]',
  panel: 'bg-[#202c33]',
  muted: 'text-[#8696a0]',
  green: 'bg-[#00a884] hover:bg-[#06cf9c]',
  red: 'bg-[#ea0038] hover:bg-[#ff1a4f]',
};

const clock = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = String(seconds % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
};

const initialsOf = (name: string | null, phone: string): string => {
  const src = name?.trim();
  if (!src) return phone.slice(-2);
  return src
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
};

const endLine = (reason: SoftphoneEndReason | null, elapsed: number): string => {
  switch (reason) {
    case 'rejected':
      return 'Call declined';
    case 'missed':
      return 'No answer';
    case 'dropped':
      return elapsed > 0 ? `Call dropped · ${clock(elapsed)}` : 'Call dropped';
    default:
      return elapsed > 0 ? `Call ended · ${clock(elapsed)}` : 'Call ended';
  }
};

const statusLine = (state: SoftphoneState): string => {
  switch (state.status) {
    case 'checking':
      return 'Checking call permission…';
    case 'dialing':
      return 'Calling…';
    case 'ringing':
      return 'Ringing…';
    case 'incoming':
      return 'Incoming voice call';
    case 'answering':
      return 'Connecting…';
    case 'connected':
      return clock(state.elapsed);
    case 'ended':
      return endLine(state.endReason, state.elapsed);
    case 'failed':
      return state.error ?? 'Call failed';
    default:
      return '';
  }
};

const RoundButton = ({
  label,
  onClick,
  className,
  size = 'md',
  pressed,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  className: string;
  size?: 'sm' | 'md' | 'lg';
  pressed?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}): React.JSX.Element => (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    aria-label={label}
    aria-pressed={pressed}
    title={label}
    disabled={disabled}
    className={`flex shrink-0 items-center justify-center rounded-full text-white transition-colors disabled:opacity-40 ${
      size === 'lg' ? 'h-16 w-16' : size === 'md' ? 'h-12 w-12' : 'h-8 w-8'
    } ${className}`}
  >
    {children}
  </button>
);

/**
 * The call's sounds: ringtone while an inbound call rings here, ringback
 * while an outgoing one rings at the customer's end, and the end tone once a
 * call that was actually up finishes.
 */
const useCallTones = (state: SoftphoneState): void => {
  const { status } = state;
  const wasLive = useRef(false);

  useEffect(() => {
    if (status === 'incoming') return startRingtone();
    // 'dialing' is ours-in-flight, 'ringing' is the customer's phone: WhatsApp
    // plays ringback through both.
    if (status === 'dialing' || status === 'ringing') return startRingback();
    return undefined;
  }, [status]);

  useEffect(() => {
    const live = status === 'connected' || status === 'ringing' || status === 'answering';
    if (live) wasLive.current = true;
    // Only after a call that got somewhere - not after a permission refusal.
    if (status === 'ended' && wasLive.current) playEndTone();
    if (status === 'idle') wasLive.current = false;
  }, [status]);
};

/**
 * Desktop notification while an inbound call rings on a hidden tab. Skipped
 * when this device gets the call as a web push already (one alert, not two).
 * Android has no `new Notification()` - it throws - so a failure here is
 * swallowed rather than taking the whole app down mid-ring.
 */
const useRingNotification = (state: SoftphoneState): void => {
  const ringing = state.status === 'incoming';
  const who = state.peer ? (state.peer.name ?? `+${state.peer.phone}`) : 'Customer';
  useEffect(() => {
    if (!ringing) return;
    let note: Notification | null = null;
    if (
      'Notification' in window &&
      Notification.permission === 'granted' &&
      document.hidden &&
      !isPushCovering('whatsappCalls')
    ) {
      try {
        note = new Notification(`Incoming WhatsApp call · ${who}`, {
          body: 'Open Utazzo to answer.',
          tag: 'whatsapp-call',
          requireInteraction: true,
        });
        note.onclick = () => {
          window.focus();
          note?.close();
        };
      } catch {
        note = null; // The call window and ringtone still carry the ring.
      }
    }
    return () => {
      note?.close();
    };
  }, [ringing, who]);
};

/** Collapsed call: a green bar at the top of the screen, like WhatsApp's "return to call". */
const CallPill = ({ softphone }: { softphone: Softphone }): React.JSX.Element => {
  const { state } = softphone;
  const incoming = state.status === 'incoming';
  const live = !['ended', 'failed'].includes(state.status);
  const name = state.peer?.name ?? (state.peer ? `+${state.peer.phone}` : 'Customer');
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => softphone.setMinimized(false)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') softphone.setMinimized(false);
      }}
      title="Return to call"
      className="fixed top-3 left-1/2 z-[60] flex max-w-[calc(100vw-2rem)] -translate-x-1/2 cursor-pointer items-center gap-3 rounded-full bg-[#00a884] py-1.5 pr-1.5 pl-2 text-white shadow-xl ring-1 ring-black/10"
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/25 text-xs font-semibold">
        {initialsOf(state.peer?.name ?? null, state.peer?.phone ?? '')}
      </span>
      <span className="flex min-w-0 flex-col leading-tight">
        <span className="truncate text-sm font-semibold">{name}</span>
        <span className="truncate text-[11px] text-white/85">
          {state.status === 'connected' ? clock(state.elapsed) : statusLine(state)}
          {state.muted && state.status === 'connected' ? ' · Muted' : ''}
        </span>
      </span>
      {incoming ? (
        <>
          <RoundButton
            label="Decline"
            onClick={() => void softphone.hangUp()}
            className={WA.red}
            size="sm"
          >
            <PhoneOff className="h-4 w-4" />
          </RoundButton>
          <RoundButton
            label="Accept"
            onClick={() => void softphone.answer()}
            className="bg-white/25 hover:bg-white/35"
            size="sm"
          >
            <Phone className="h-4 w-4" />
          </RoundButton>
        </>
      ) : live ? (
        <>
          <RoundButton
            label={state.muted ? 'Unmute' : 'Mute'}
            onClick={softphone.toggleMute}
            pressed={state.muted}
            disabled={state.status === 'checking' || state.status === 'answering'}
            className={state.muted ? 'bg-white !text-[#00a884]' : 'bg-white/20 hover:bg-white/30'}
            size="sm"
          >
            {state.muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
          </RoundButton>
          <RoundButton
            label="End call"
            onClick={() => void softphone.hangUp()}
            className={WA.red}
            size="sm"
          >
            <PhoneOff className="h-4 w-4" />
          </RoundButton>
        </>
      ) : (
        <RoundButton
          label="Close"
          onClick={softphone.dismiss}
          className="bg-white/20 hover:bg-white/30"
          size="sm"
        >
          <X className="h-4 w-4" />
        </RoundButton>
      )}
    </div>
  );
};

/**
 * The call window - WhatsApp desktop's dark call screen. Floats over the chat
 * widget's corner and survives page changes (it is rendered by the widget
 * root). Minimise collapses it to a green pill at the top so the agent can
 * keep working - chat, open the lead - mid-call.
 */
export const WhatsAppCallScreen = ({
  softphone,
}: {
  softphone: Softphone;
}): React.JSX.Element | null => {
  const { state } = softphone;
  useCallTones(state);
  useRingNotification(state);
  if (state.status === 'idle') return null;
  if (state.minimized) return <CallPill softphone={softphone} />;

  const over = state.status === 'ended' || state.status === 'failed';
  const incoming = state.status === 'incoming';
  // Answering takes a calling line too, so a full pool blocks Accept.
  const linesFull = state.lines ? state.lines.busy >= state.lines.total : false;
  const waiting = ['checking', 'dialing', 'ringing', 'incoming', 'answering'].includes(
    state.status,
  );
  const name = state.peer?.name ?? (state.peer ? `+${state.peer.phone}` : 'Customer');

  return (
    <div
      role="dialog"
      aria-label="WhatsApp call"
      // Phones: the whole screen, like a real incoming call. Wider screens:
      // the floating card above the WhatsApp bubble, as before.
      className={`fixed inset-0 z-[60] flex flex-col overflow-hidden text-white shadow-2xl ring-1 ring-black/40 sm:inset-auto sm:right-5 sm:bottom-24 sm:h-[600px] sm:max-h-[75vh] sm:w-[380px] sm:max-w-[calc(100vw-2.5rem)] sm:rounded-2xl ${WA.bg}`}
    >
      {/* Header: minimise + label */}
      <div className="flex items-center justify-between px-3 pt-3">
        <button
          type="button"
          onClick={() => softphone.setMinimized(true)}
          aria-label="Minimise call"
          title="Minimise - keep working while on the call"
          className="rounded-full p-2 text-[#aebac1] transition-colors hover:bg-white/10 hover:text-white"
        >
          <ChevronDown className="h-5 w-5" />
        </button>
        <span className={`text-xs ${WA.muted}`}>WhatsApp voice call</span>
        <span className="w-9" />
      </div>

      {/* Caller */}
      <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center">
        <span className="relative mb-4 flex h-28 w-28 items-center justify-center">
          {waiting && (
            <>
              <span className="absolute inset-0 animate-ping rounded-full bg-[#00a884]/20" />
              <span className="absolute -inset-3 rounded-full border border-[#00a884]/25" />
            </>
          )}
          <span className="relative flex h-28 w-28 items-center justify-center rounded-full bg-[#6a7175] text-3xl font-semibold">
            {initialsOf(state.peer?.name ?? null, state.peer?.phone ?? '')}
          </span>
        </span>
        <span className="max-w-full truncate text-2xl font-normal">{name}</span>
        {state.peer?.name && <span className={`text-sm ${WA.muted}`}>+{state.peer.phone}</span>}
        <span
          aria-live="polite"
          className={`mt-1 text-sm ${
            state.status === 'failed' ? 'text-rose-300' : WA.muted
          } ${state.status === 'connected' ? 'font-mono text-base text-white' : ''}`}
        >
          {statusLine(state)}
        </span>
        {state.status === 'connected' && state.muted && (
          <span className="mt-1 rounded-full bg-white/10 px-3 py-1 text-[11px]">You are muted</span>
        )}
        {incoming && linesFull && (
          <span className="mt-1 rounded-full bg-white/10 px-3 py-1 text-[11px] text-amber-200">
            All {state.lines?.total} calling lines are busy
          </span>
        )}
      </div>

      {/* Controls */}
      <div className="px-5 pb-6">
        {incoming ? (
          <div className="flex items-end justify-around">
            <div className="flex flex-col items-center gap-2">
              <RoundButton
                label="Decline"
                onClick={() => void softphone.hangUp()}
                className={WA.red}
                size="lg"
              >
                <PhoneOff className="h-7 w-7" />
              </RoundButton>
              <span className={`text-xs ${WA.muted}`}>Decline</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <RoundButton
                label="Accept"
                onClick={() => void softphone.answer()}
                disabled={linesFull}
                className={`${WA.green} ${linesFull ? '' : 'animate-bounce'}`}
                size="lg"
              >
                <Phone className="h-7 w-7" />
              </RoundButton>
              <span className={`text-xs ${WA.muted}`}>Accept</span>
            </div>
          </div>
        ) : over ? (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={softphone.dismiss}
              className={`flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-medium transition-colors hover:bg-white/15 ${WA.panel}`}
            >
              <X className="h-4 w-4" /> Close
            </button>
          </div>
        ) : (
          <div
            className={`flex items-center justify-center gap-6 rounded-full px-6 py-3 ${WA.panel}`}
          >
            <RoundButton
              label={state.muted ? 'Unmute' : 'Mute'}
              onClick={softphone.toggleMute}
              pressed={state.muted}
              disabled={state.status === 'checking' || state.status === 'answering'}
              className={
                state.muted ? 'bg-white !text-[#0b141a]' : 'bg-[#374248] hover:bg-[#435058]'
              }
            >
              {state.muted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
            </RoundButton>
            <RoundButton
              label="End call"
              onClick={() => void softphone.hangUp()}
              className={WA.red}
            >
              <PhoneOff className="h-5 w-5" />
            </RoundButton>
          </div>
        )}
      </div>
    </div>
  );
};
